"use client";

import { ENVELOPE_DURATION_CAP_MINUTES } from "@repo/domain";
import { PropertyPicker, PropertyPickerList, type PropertyPickerOption } from "@repo/ui/components/property-picker";
import { cn } from "@repo/ui/lib/utils";

export const DURATION_OPTIONS: { label: string; value: number; unit: "min" | "h" }[] = [
  { label: "5 minutes", value: 5, unit: "min" },
  { label: "10 minutes", value: 10, unit: "min" },
  { label: "15 minutes", value: 15, unit: "min" },
  { label: "20 minutes", value: 20, unit: "min" },
  { label: "25 minutes", value: 25, unit: "min" },
  { label: "30 minutes", value: 30, unit: "min" },
  { label: "35 minutes", value: 35, unit: "min" },
  { label: "40 minutes", value: 40, unit: "min" },
  { label: "45 minutes", value: 45, unit: "min" },
  { label: "50 minutes", value: 50, unit: "min" },
  { label: "55 minutes", value: 55, unit: "min" },
  { label: "1 hour", value: 60, unit: "min" },
];

function plural(count: number, unit: string) {
  return `${count} ${unit}${count === 1 ? "" : "s"}`;
}

function durationLabel(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (hours === 0) return plural(remainder, "minute");
  return remainder === 0 ? plural(hours, "hour") : `${plural(hours, "hour")} ${plural(remainder, "minute")}`;
}

const PICKER_OPTIONS: PropertyPickerOption<number>[] = [
  ...DURATION_OPTIONS.map((option) => ({ value: option.value, label: option.label })),
  ...Array.from({ length: (ENVELOPE_DURATION_CAP_MINUTES - 60) / 5 }, (_, index) => {
    const minutes = 65 + index * 5;
    return { value: minutes, label: durationLabel(minutes) };
  }),
];

export interface DurationPickerProps {
  /** Minutes. A value off the option list still displays. */
  value: number;
  onChange: (minutes: number) => void;
  /** Accessible name, read before the value. */
  label: string;
  /** Options below this are left out of the list; the current value still displays. */
  min?: number;
  disabled?: boolean;
  className?: string;
}

export function DurationPicker({ value, onChange, label, min, disabled, className }: DurationPickerProps) {
  const listed = PICKER_OPTIONS.map((option) =>
    min !== undefined && option.value < min ? { ...option, hidden: true } : option,
  );
  const options = listed.some((option) => option.value === value)
    ? listed
    : [...listed, { value, label: durationLabel(value), hidden: true }];
  return (
    <PropertyPicker<number>
      value={value}
      onChange={(minutes) => {
        if (minutes !== value) onChange(minutes);
      }}
      disabled={disabled}
      className={cn("h-6 rounded-md px-1.5 font-medium tabular-nums", className)}
      displayValue={
        <>
          <span className="sr-only">{label} </span>
          <span>{value} min</span>
        </>
      }
    >
      <PropertyPickerList<number> options={options} />
    </PropertyPicker>
  );
}
